import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';

import { FaqAccordion, type FaqItem } from '../components/FaqAccordion';
import { Footer } from '../components/Footer';
import { socialLinks } from '../config/socialLinks';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { trackButtonClick, trackPageView } from '../services/telemetry';

const linkClass = 'text-orange-600 underline hover:text-blue-800';

const CONTACT_EMAIL = 'hello@pineapplemusiclab.com';

const SUPPORT_FAQ_ITEMS: FaqItem[] = [
  {
    question: 'What do piano fingering numbers mean?',
    answer:
      'Piano fingering uses numbers 1–5 for each hand: 1 = thumb, 2 = index finger, 3 = middle finger, 4 = ring finger, 5 = pinky. These numbers appear above the staff (right hand) or below the staff (left hand) next to the notes on the score.',
  },
  {
    question: 'Are my changes saved automatically?',
    answer: (
      <>
        Yes. When you click <strong>Done</strong> in the edit-mode toolbar (or
        tap the side-toggle <strong>pencil pill</strong> a second time to exit
        edit mode), your edits are saved to the server automatically. If a save
        fails you stay in edit mode so you can retry without losing your pending
        edits.
      </>
    ),
    plainTextAnswer:
      'Yes. When you click Done in the edit-mode toolbar (or tap the side-toggle pencil pill a second time to exit edit mode), your edits are saved to the server automatically. If a save fails you stay in edit mode so you can retry without losing your pending edits.',
  },
  {
    question: 'Can I print or export my annotated sheet music?',
    answer: (
      <>
        Yes. PDF projects have a <strong>PDF</strong> button that downloads a
        printable PDF of the score as you see it, and an <strong>XML</strong>{' '}
        button. That menu offers <strong>MusicXML</strong> (the score without
        fingering numbers, including digital PDFs),{' '}
        <strong>MusicXML with Fingerings</strong>, and{' '}
        <strong>MusicXML with Fingerings (Sibelius)</strong>. MusicXML projects
        have a <strong>PDF</strong> button for an engraved PDF, an XML button
        with <strong>MusicXML with Fingerings</strong> and{' '}
        <strong>MusicXML with Fingerings (Sibelius)</strong>, and a{' '}
        <strong>MIDI</strong> button once the score has loaded.
      </>
    ),
    plainTextAnswer:
      'Yes. PDF projects have a PDF button that downloads a printable PDF of the score as you see it, and an XML button. That menu offers MusicXML (the score without fingering numbers, including digital PDFs), MusicXML with Fingerings, and MusicXML with Fingerings (Sibelius). MusicXML projects have a PDF button for an engraved PDF, an XML button with MusicXML with Fingerings and MusicXML with Fingerings (Sibelius), and a MIDI button once the score has loaded.',
  },
  {
    question: 'What kind of PDF works best?',
    answer:
      "Digitally exported PDFs (from notation software or a publisher's digital edition) produce the best results. High-quality scanned sheet music also works well. Photos are accepted too: a full, evenly lit page works best. Skewed angles, dim lighting, and low resolution can reduce accuracy.",
  },
  {
    question: 'What is a credit?',
    answer:
      'A credit is used each time you generate fingerings for a score. One credit is consumed per generation, regardless of the score length (within your plan limits).',
  },
  {
    question: 'What happens when I run out of credits?',
    answer:
      'If you run out of credits, you can purchase a credit pack or wait for your monthly credits to reset at your next billing cycle. You can also still generate fingerings with free-tier limits (3 systems for PDF or page images, 18 measures for MusicXML).',
  },
  {
    question: 'Can I cancel my subscription?',
    answer:
      'Yes, you can cancel anytime from My Profile using Manage billing. If you have a scheduled plan change (e.g., a pending downgrade), cancel it first using the "Cancel Change" button next to the scheduled change notice, then use Manage billing to cancel your subscription. Your plan benefits continue until the end of your current billing period. Extra credits from credit packs are preserved even after cancellation.',
  },
  {
    id: 'delete-account',
    question: 'How do I delete my Pineapple Music Lab account and data?',
    answer: (
      <>
        To request deletion of your Pineapple Music Lab account, please email us
        at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
          {CONTACT_EMAIL}
        </a>
        . We&apos;ll process your request and confirm by email, typically within
        2 business days.
      </>
    ),
    plainTextAnswer:
      "To request deletion of your Pineapple Music Lab account, please email us at hello@pineapplemusiclab.com. We'll process your request and confirm by email, typically within 2 business days.",
  },
];

function Support() {
  usePageMeta(getPublicPageMeta('/support'));

  useEffect(() => {
    trackPageView('Support', '/support');
  }, []);

  return (
    <>
      <main
        className={`
          flex min-h-screen flex-col items-center bg-[#fbfcfc] p-4 font-[Outfit]
        `}
      >
        <header className="mb-10 w-full max-w-6xl text-left">
          <h1
            className={`
              mb-3 text-3xl font-light text-[#0d0d0d]
              sm:mb-4 sm:text-4xl
              lg:text-[40px]
            `}
          >
            Support
          </h1>
          <p className="text-base font-light text-[#0d0d0d] sm:text-lg">
            We&apos;re here to help.
          </p>
        </header>

        <article className="mb-12 flex w-full max-w-6xl flex-col gap-12">
          <section
            aria-labelledby="contact-heading"
            className="flex flex-col gap-6"
          >
            <h2
              id="contact-heading"
              className={`
                text-left text-2xl font-light text-[#0d0d0d]
                sm:text-3xl
              `}
            >
              Need help with PineappleMusicLab?
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Email card */}
              <div
                className={`
                  flex flex-col rounded-r-lg border-l-4 border-yellow-400
                  bg-yellow-50 p-6 text-left text-gray-800 shadow-sm
                  sm:p-8
                `}
              >
                <div className="mb-3 flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="text-2xl text-yellow-600"
                    aria-hidden="true"
                  />
                  <h3 className="text-lg font-semibold sm:text-xl">Email us</h3>
                </div>
                <p className="mb-2 text-base sm:text-lg">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    onClick={async () =>
                      await trackButtonClick('Support', 'Contact: Email')
                    }
                    className={linkClass}
                  >
                    {CONTACT_EMAIL}
                  </a>
                </p>
                <p className="m-0 text-sm text-gray-600 sm:text-base">
                  We typically respond within 1-3 business days.
                </p>
              </div>

              {/* Discord card */}
              <div
                className={`
                  flex flex-col rounded-r-lg border-l-4 border-indigo-500
                  bg-indigo-50 p-6 text-left text-gray-800 shadow-sm
                  sm:p-8
                `}
              >
                <div className="mb-3 flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faDiscord}
                    className="text-2xl text-indigo-600"
                    aria-hidden="true"
                  />
                  <h3 className="text-lg font-semibold sm:text-xl">
                    Join our community
                  </h3>
                </div>
                <p className="mb-4 text-sm text-gray-600 sm:text-base">
                  Get help from the community and our team on our Discord
                  server.
                </p>
                <a
                  href={socialLinks.discordServer}
                  onClick={async () =>
                    await trackButtonClick('Support', 'Contact: Discord')
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    mt-auto inline-flex items-center justify-center gap-2
                    self-start rounded-lg bg-yellow-400 px-5 py-2.5 text-base
                    font-semibold text-gray-800 transition-transform
                    duration-100
                    hover:bg-yellow-600
                    active:scale-95 active:bg-yellow-700
                    sm:text-lg
                  `}
                >
                  <FontAwesomeIcon icon={faDiscord} aria-hidden="true" />
                  <span>Join Discord Server</span>
                </a>
              </div>
            </div>
          </section>

          {/* Quick links row */}
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="m-0 text-sm font-semibold text-gray-500 uppercase">
              Other helpful resources
            </p>
            <nav
              aria-label="Other helpful resources"
              className={`
                flex flex-wrap items-center justify-center gap-x-6 gap-y-2
              `}
            >
              <Link
                to="/tutorial"
                onClick={async () =>
                  await trackButtonClick('Support', 'QuickLinks: Tutorial')
                }
                className={linkClass}
              >
                Tutorial
              </Link>
              <Link
                to="/pricing"
                onClick={async () =>
                  await trackButtonClick('Support', 'QuickLinks: Pricing')
                }
                className={linkClass}
              >
                Pricing
              </Link>
              <Link
                to="/privacy-policy"
                onClick={async () =>
                  await trackButtonClick(
                    'Support',
                    'QuickLinks: Privacy Policy'
                  )
                }
                className={linkClass}
              >
                Privacy Policy
              </Link>
            </nav>
          </div>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          {/* FAQ */}
          <section className="flex flex-col gap-4">
            {/*
              emitJsonLd={false}: these Q&A are duplicates of items already
              published on /tutorial and /pricing (their canonical FAQ pages).
              Skipping the FAQPage JSON-LD here avoids competing with those
              pages for the same Google rich result.
            */}
            <FaqAccordion
              items={SUPPORT_FAQ_ITEMS}
              emitJsonLd={false}
              heading="Frequently asked questions"
              variant="flat"
              align="left"
              maxWidthClass="max-w-6xl"
            />

            <p className="text-center text-sm text-gray-600 sm:text-base">
              Looking for more? See the full{' '}
              <Link
                to="/tutorial#faq"
                onClick={async () =>
                  await trackButtonClick('Support', 'SeeMore: Tutorial FAQ')
                }
                className={linkClass}
              >
                Tutorial FAQ
              </Link>{' '}
              or the{' '}
              <Link
                to="/pricing#faq-heading"
                onClick={async () =>
                  await trackButtonClick('Support', 'SeeMore: Pricing FAQ')
                }
                className={linkClass}
              >
                Pricing FAQ
              </Link>
              .
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
}

export { Support };
